/*!
The MIT License

Copyright © 2010-2025 three.js authors

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in
all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
THE SOFTWARE.
*/
import * as THREE from './three'

// Adapted from Was Never Built applyWaterMaterials.ts and Three.js Water.js
// (MIT, Slayvin / stemkoski / 29a.ch). Static reflection: no extra scene pass.
export const isWaterWall = node => node.name === 'mesh' && node.id === 'water_wall' && !!node._geometry

const vertexShader = `
				uniform mat4 textureMatrix;
				uniform float time;

				varying vec4 mirrorCoord;
				varying vec4 worldPosition;
 varying vec3 waterWorldNormal;

				#include <common>
				#include <fog_pars_vertex>
				#include <shadowmap_pars_vertex>
				#include <logdepthbuf_pars_vertex>

				void main() {
					mirrorCoord = modelMatrix * vec4( position, 1.0 );
					worldPosition = mirrorCoord.xyzw;

					mirrorCoord = textureMatrix * mirrorCoord;
					vec4 mvPosition =  modelViewMatrix * vec4( position, 1.0 );
					gl_Position = projectionMatrix * mvPosition;

				#include <beginnormal_vertex>
				#include <defaultnormal_vertex>
 waterWorldNormal = inverseTransformDirection(transformedNormal, viewMatrix);
				#include <logdepthbuf_vertex>
				#include <fog_vertex>
				#include <shadowmap_vertex>
			}`
const fragmentShader = `
				uniform sampler2D mirrorSampler;
				uniform float alpha;
				uniform float time;
				uniform float size;
				uniform float distortionScale;
				uniform sampler2D normalSampler;
				uniform vec3 sunColor;
				uniform vec3 sunDirection;
				
				uniform vec3 waterColor;

				varying vec4 mirrorCoord;
				varying vec4 worldPosition;
 varying vec3 waterWorldNormal;

				vec4 getNoise( vec2 uv ) {
					vec2 uv0 = ( uv / 103.0 ) + vec2(time / 17.0, time / 29.0);
					vec2 uv1 = uv / 107.0-vec2( time / -19.0, time / 31.0 );
					vec2 uv2 = uv / vec2( 8907.0, 9803.0 ) + vec2( time / 101.0, time / 97.0 );
					vec2 uv3 = uv / vec2( 1091.0, 1027.0 ) - vec2( time / 109.0, time / -113.0 );
					vec4 noise = texture2D( normalSampler, uv0 ) +
						texture2D( normalSampler, uv1 ) +
						texture2D( normalSampler, uv2 ) +
						texture2D( normalSampler, uv3 );
					return noise * 0.5 - 1.0;
				}

				void sunLight( const vec3 surfaceNormal, const vec3 eyeDirection, float shiny, float spec, float diffuse, inout vec3 diffuseColor, inout vec3 specularColor ) {
					vec3 reflection = normalize( reflect( -sunDirection, surfaceNormal ) );
					float direction = max( 0.0, dot( eyeDirection, reflection ) );
					specularColor += pow( direction, shiny ) * sunColor * spec;
					diffuseColor += max( dot( sunDirection, surfaceNormal ), 0.0 ) * sunColor * diffuse;
				}

				#include <common>
				#include <packing>
				#include <bsdfs>
				#include <fog_pars_fragment>
				#include <logdepthbuf_pars_fragment>
				#include <lights_pars_begin>
				#include <shadowmap_pars_fragment>
				#include <shadowmask_pars_fragment>

				void main() {

					#include <logdepthbuf_fragment>
					vec3 baseNormal = normalize(waterWorldNormal);
                    vec3 reference = abs(baseNormal.y) < 0.9 ? vec3(0.0, 1.0, 0.0) : vec3(0.0, 0.0, 1.0);
                    vec3 tangent = normalize(cross(reference, baseNormal));
                    vec3 bitangent = cross(baseNormal, tangent);
                    vec2 waterUV = vec2(dot(worldPosition.xyz, tangent), dot(worldPosition.xyz, bitangent));
                    vec4 noise = getNoise(waterUV * size);
                    vec3 surfaceNormal = normalize(tangent * noise.x * 1.5 + baseNormal * noise.z + bitangent * noise.y * 1.5);

					vec3 diffuseLight = vec3(0.0);
					vec3 specularLight = vec3(0.0);

					vec3 worldToEye = cameraPosition-worldPosition.xyz;
					vec3 eyeDirection = normalize( worldToEye );
					sunLight( surfaceNormal, eyeDirection, 100.0, 2.0, 0.5, diffuseLight, specularLight );

					float distance = max(length(worldToEye), 0.01);

					vec2 distortion = surfaceNormal.xz * ( 0.001 + 1.0 / distance ) * distortionScale;
					vec3 reflectionSample = texture2D(mirrorSampler, vec2(0.5)).rgb;

					float theta = max( dot( eyeDirection, surfaceNormal ), 0.0 );
					float rf0 = 0.3;
					float reflectance = rf0 + ( 1.0 - rf0 ) * pow( ( 1.0 - theta ), 5.0 );
					vec3 scatter = max( 0.0, dot( surfaceNormal, eyeDirection ) ) * waterColor;
					vec3 albedo = mix( ( sunColor * diffuseLight * 0.3 + scatter ) * getShadowMask(), ( vec3( 0.1 ) + reflectionSample * 0.9 + reflectionSample * specularLight ), reflectance);
					vec3 outgoingLight = albedo;
					gl_FragColor = vec4( outgoingLight, alpha );

					#include <tonemapping_fragment>
					#include <colorspace_fragment>
					#include <fog_fragment>	
				}`

export class WaterWalls {
  constructor(world) {
    this.world = world
    this.time = 0
    this.materials = []
  }

  async prepare(root) {
    const nodes = []
    root.traverse(node => { if (isWaterWall(node)) nodes.push(node) })
    if (!nodes.length) return
    // Clone the cached texture: repeat/color settings belong only to this effect.
    const normals = (await this.world.loader.load('texture', '/textures/waternormals.jpg')).clone()
    normals.wrapS = normals.wrapT = THREE.RepeatWrapping
    normals.colorSpace = THREE.NoColorSpace
    normals.needsUpdate = true
    const reflection = new THREE.DataTexture(new Uint8Array([135, 210, 240, 255]), 1, 1, THREE.RGBAFormat)
    reflection.needsUpdate = true
    for (const node of nodes) {
      node.linked = false
      node.castShadow = false
      node.material = new THREE.ShaderMaterial({
        name: 'SaratogaWaterWall', vertexShader, fragmentShader,
        lights: true, side: THREE.DoubleSide, transparent: true,
        depthWrite: false, blending: THREE.NormalBlending,
        uniforms: {
          ...THREE.UniformsUtils.merge([THREE.UniformsLib.fog, THREE.UniformsLib.lights]),
          normalSampler: { value: normals }, mirrorSampler: { value: reflection },
          alpha: { value: 0.05 }, time: { value: 0 }, size: { value: 18 },
          distortionScale: { value: 1.25 }, textureMatrix: { value: new THREE.Matrix4() },
          sunColor: { value: new THREE.Color(0xe8ffff) },
          sunDirection: { value: new THREE.Vector3(0.45, 0.8, 0.35).normalize() },
          waterColor: { value: new THREE.Color(0x20b4e5) },
        },
      })
    }
  }

  mounted() {
    // Stage clones materials when inserting standalone meshes. Animate those
    // rendered copies, not just the source material retained by the GLB node.
    this.world.stage.scene.traverse(object => {
      if (object.material?.name === 'SaratogaWaterWall') this.materials.push(object.material)
    })
  }

  update(delta) {
    this.time += delta * 0.8
    for (const material of this.materials) material.uniforms.time.value = this.time
  }
}
